import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getOrganisationDetails,
  uploadOrganisationBranding,
} from "../../services/organizationService";
import { Organisation } from "../../types/types";
import { getErrorMessage } from "../../services/restApiService";
import { useToast } from "../../components/CustomToast";
import { useUserProfile } from "../../context/UserProfileContext";

export const useBrandingHook = () => {
  const { organisationId } = useParams<{ organisationId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { userProfile } = useUserProfile();
  const isReadOnly =
    userProfile?.role === "super_admin" &&
    !!organisationId &&
    organisationId !== String(userProfile?.organisation_id);

  // State management
  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [currentLogo, setCurrentLogo] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Fetch organization details
  const fetchOrganisationDetails = useCallback(async () => {
    if (!organisationId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await getOrganisationDetails(organisationId);
      setOrganisation(response);
      setCurrentLogo(response.logo_url || "");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [organisationId]);

  // Handle file selection
  const handleFileSelected = (file: File | null) => {
    setSelectedFile(file);
    if (file) {
      const fileUrl = URL.createObjectURL(file);
      setCurrentLogo(fileUrl);
    } else {
      setCurrentLogo("");
    }
  };

  // Handle save logo
  const handleSave = async () => {
    if (!organisationId) {
      toast.showToast("Organization ID is required", "error");
      return;
    }

    if (!selectedFile) {
      toast.showToast("Please select a logo file to upload", "error");
      return;
    }

    setIsUploading(true);
    try {
      const { logoUrl } = await uploadOrganisationBranding(
        organisationId,
        selectedFile
      );
      if (logoUrl) {
        setCurrentLogo(logoUrl);
      }
      toast.showToast("Logo uploaded successfully!", "success");
      // Navigate back to organization details
      navigate(`/organisation/${organisationId}`);
    } catch (err) {
      toast.showToast(getErrorMessage(err), "error");
    } finally {
      setIsUploading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate(`/organisation/${organisationId}`);
  };

  // Initialize data on component mount
  useEffect(() => {
    if (organisationId) {
      fetchOrganisationDetails();
    }
  }, [organisationId, fetchOrganisationDetails]);

  return {
    // State
    organisation,
    loading,
    error,
    isUploading,
    currentLogo,
    selectedFile,
    isReadOnly,

    // Actions
    handleFileSelected,
    handleSave,
    handleCancel,
  };
};
